import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../../theme';
import { EmojiPicker } from '../../EmojiPicker';
import { htmlToWhatsAppMarkdown } from '../../../utils/htmlToWhatsAppMarkdown';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

interface TemplateBodyEditorProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  error?: string;
  maxLength?: number;
}

const MIN_HEIGHT = 100;
const MAX_HEIGHT = 260;

/**
 * Body text editor for WhatsApp templates. WhatsApp templates don't support
 * true rich text — bold/italic/strikethrough/monospace and bullet lists are
 * plain markdown-style markers WhatsApp renders client-side (*bold*,
 * _italic_, ~strike~, ```mono```, "- " for bullets). This editor inserts/
 * wraps those markers at the current cursor/selection, and auto-expands with
 * content like a chat composer.
 */
export const TemplateBodyEditor: React.FC<TemplateBodyEditorProps> = ({
  label,
  value,
  onChangeText,
  placeholder,
  error,
  maxLength = 1024,
}) => {
  const styles = useThemedStyles(getStyles);
  const inputRef = useRef<TextInput>(null);
  const [height, setHeight] = useState(MIN_HEIGHT);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const wrapSelection = (marker: string) => {
    const { start, end } = selection;
    const selected = value.slice(start, end);
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = `${before}${marker}${selected || ''}${marker}${after}`;
    onChangeText(next);
    const cursor = selected ? end + marker.length * 2 : start + marker.length;
    requestAnimationFrame(() => {
      inputRef.current?.setNativeProps({ selection: { start: cursor, end: cursor } });
    });
  };

  const insertBullet = () => {
    const { start } = selection;
    const before = value.slice(0, start);
    const after = value.slice(start);
    // Start the bullet on its own line
    const needsNewline = before.length > 0 && !before.endsWith('\n');
    const prefix = `${needsNewline ? '\n' : ''}- `;
    const next = `${before}${prefix}${after}`;
    onChangeText(next);
    const cursor = start + prefix.length;
    requestAnimationFrame(() => {
      inputRef.current?.setNativeProps({ selection: { start: cursor, end: cursor } });
    });
  };

  const insertEmoji = (emoji: string) => {
    const { start } = selection;
    const before = value.slice(0, start);
    const after = value.slice(start);
    const next = `${before}${emoji}${after}`;
    onChangeText(next);
    setShowEmojiPicker(false);
    const cursor = start + emoji.length;
    requestAnimationFrame(() => {
      inputRef.current?.setNativeProps({ selection: { start: cursor, end: cursor } });
    });
  };

  const handleContentSizeChange = (e: any) => {
    const contentHeight = e.nativeEvent.contentSize.height;
    const padding = Platform.OS === 'ios' ? 16 : 12;
    setHeight(Math.min(Math.max(contentHeight + padding, MIN_HEIGHT), MAX_HEIGHT));
  };

  // On web, react-native-web forwards TextInput's ref straight to the
  // underlying <textarea>, so we can listen for the native browser `paste`
  // event to read the clipboard's HTML representation (e.g. from Word/Google
  // Docs) and convert its formatting into WhatsApp's markdown syntax before
  // it lands in the field. RN's own onPaste (native + web synthetic) only
  // exposes plain text, so this has to bypass it entirely.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (!node || typeof node.addEventListener !== 'function') return;

    const handleNativePaste = (event: ClipboardEvent) => {
      const html = event.clipboardData?.getData('text/html');
      if (!html || !html.trim()) return; // no rich content on the clipboard — let the default plain-text paste proceed

      event.preventDefault();
      const converted = htmlToWhatsAppMarkdown(html);
      if (!converted) return;

      const start = node.selectionStart ?? value.length;
      const end = node.selectionEnd ?? value.length;
      const next = `${value.slice(0, start)}${converted}${value.slice(end)}`.slice(0, maxLength);
      onChangeText(next);

      const cursor = Math.min(start + converted.length, maxLength);
      requestAnimationFrame(() => {
        node.setSelectionRange?.(cursor, cursor);
      });
    };

    node.addEventListener('paste', handleNativePaste);
    return () => node.removeEventListener('paste', handleNativePaste);
  }, [value, maxLength, onChangeText]);

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.toolbar}>
        <TouchableOpacity style={styles.toolButton} onPress={() => wrapSelection('*')} activeOpacity={0.7}>
          <Text style={styles.toolButtonBoldText}>B</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.toolButton} onPress={() => wrapSelection('_')} activeOpacity={0.7}>
          <Text style={styles.toolButtonItalicText}>I</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.toolButton} onPress={() => wrapSelection('~')} activeOpacity={0.7}>
          <Text style={styles.toolButtonStrikeText}>S</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.toolButton} onPress={insertBullet} activeOpacity={0.7}>
          <Ionicons name="list-outline" size={18} color={colors.textDark} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.toolButton} onPress={() => setShowEmojiPicker(true)} activeOpacity={0.7}>
          <Ionicons name="happy-outline" size={18} color={colors.textDark} />
        </TouchableOpacity>
        <Text style={styles.charCount}>{value.length}/{maxLength}</Text>
      </View>
      <TextInput
        ref={inputRef}
        style={[styles.input, { height }, error && styles.inputError]}
        value={value}
        onChangeText={onChangeText}
        onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
        onContentSizeChange={handleContentSizeChange}
        placeholder={placeholder}
        placeholderTextColor={colors.textLight}
        multiline
        maxLength={maxLength}
        textAlignVertical="top"
        scrollEnabled
      />
      {error && <Text style={styles.errorText}>{error}</Text>}

      <EmojiPicker visible={showEmojiPicker} onEmojiSelect={insertEmoji} onClose={() => setShowEmojiPicker(false)} />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: { gap: spacing.xs },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  toolButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  toolButtonBoldText: { fontSize: 14, fontWeight: '800', color: colors.textDark },
  toolButtonItalicText: { fontSize: 14, fontStyle: 'italic', color: colors.textDark },
  toolButtonStrikeText: { fontSize: 14, color: colors.textDark, textDecorationLine: 'line-through' },
  charCount: {
    marginLeft: 'auto',
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.surface,
  },
  inputError: {
    borderColor: colors.error,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },
});
